import { type Allowance, type ProductDeposit } from "@kanpla/api-contract";

import { type Currency, type Discount } from "~/api/products/types";
import { type PosProduct } from "~/types/product";
import {
  computeBasket,
  discountedExclVat,
  orderDiscountableTotal,
  type SaleEngineLine,
  type SaleEngineLineDiscount,
  type SaleEnginePricedLine
} from "~/lib/sale-engine";
import { calculateUnitPriceByUnitSystem } from "~/utils/calculateUnitPriceByUnitSystem";
import { calculateVariantTotal } from "~/utils/calculateVariantTotal";
import { inferStockStatusFromBasket } from "~/utils/stock/infer-stock-status";
import { priceFormatter } from "~/utils/price-formatter";
import { roundAwayFromZero } from "~/utils/round-away-from-zero";

import { type PosProductInBasket } from "../../types";
import { type ProductBasket } from "../../store.types";

type DefaultTaxRate = {
  vatRate: number;
  name: string | null;
  id: string | null;
};

type OrderDiscount = { amount: number; type: "percentage" | "currency" };

export type ComputeProductsInBasketArgs = {
  basket: ProductBasket[];
  products: PosProduct[];
  discounts: Discount[];
  allowances: Allowance[];
  orderDiscount: OrderDiscount;
  defaultTaxRate: DefaultTaxRate;
  currency: Currency;
  locale: string | null;
  showPricesInclVat: boolean;
};

export type ComputeProductsInBasketResult = {
  productsInBasket: PosProductInBasket[];
  totalUnit: number;
  totalUnitDiscounted: number;
  totalUnitExclVatDiscounted: number;
  /** Deposits across the basket, apart from the product totals. */
  totalDeposit: number;
  totalDepositExclVat: number;
  /** The order discount the engine applied, after its cap. */
  totalOrderDiscount: number;
  /** What an order discount can take: eligible lines, before line discounts. */
  totalOrderDiscountable: number;
};

type PricedOption = {
  choices: { amount: number; unitPriceWithVat?: number }[];
};

/**
 * Sum each option choice's incl price rounded to whole minor units, mirroring
 * the server's `calculateConfigTotal` (round per option, then × amount, round).
 */
function sumOptionChoicesInclVat(options: PricedOption[]): number {
  return options
    .flatMap((option) => option.choices)
    .reduce(
      (sum, choice) =>
        choice.amount > 0
          ? sum +
            roundAwayFromZero(
              roundAwayFromZero(choice.unitPriceWithVat ?? 0) * choice.amount
            )
          : sum,
      0
    );
}

function displayValue(
  showPricesInclVat: boolean,
  inclVat: number,
  exclVat: number
): number {
  return showPricesInclVat ? inclVat : exclVat;
}

/** The percentage shown for an order-discounted line. */
function orderDiscountPercentage(
  orderDiscount: OrderDiscount,
  orderDiscountAmount: number,
  lineTotalBeforeDiscount: number
): number {
  if (orderDiscount.type === "percentage") {
    return orderDiscount.amount;
  }
  return lineTotalBeforeDiscount > 0
    ? (orderDiscountAmount * 100) / lineTotalBeforeDiscount
    : 0;
}

type DisplayContext = {
  showPricesInclVat: boolean;
  currency: Currency;
  locale: string | null;
  hasOrderDiscount: boolean;
  orderDiscount: OrderDiscount;
};

function pricedDisplayFields(
  priced: SaleEnginePricedLine,
  exclVatBase: number,
  { showPricesInclVat, currency, locale, hasOrderDiscount }: DisplayContext
) {
  const totalPrice = priced.lineTotal;
  const before = priced.lineTotalBeforeDiscount;
  // PWEB-132: scale excl by the incl discount factor so VAT (incl − excl) stays ≥ 0.
  const totalUnitPriceExclVat = discountedExclVat(
    exclVatBase,
    totalPrice,
    before
  );
  const format = (value: number) => priceFormatter({ value, currency, locale });

  return {
    totalPrice,
    totalUnitPriceExclVat,
    // With an order discount the displayed price stays pre-discount — reduction
    // surfaces via discountUnitPrice (prior behavior).
    totalDisplayPrice: format(
      hasOrderDiscount
        ? displayValue(showPricesInclVat, before, exclVatBase)
        : displayValue(showPricesInclVat, totalPrice, totalUnitPriceExclVat)
    ),
    totalOriginalDisplayPrice: format(
      displayValue(showPricesInclVat, before, exclVatBase)
    )
  };
}

type ProductLineItem = Exclude<ProductBasket, { type: "custom" }>;
type VariantDeposits = Record<string, ProductDeposit>;
type UnitDeposit = { inclVat: number; exclVat: number };

function pickedVariantDeposits(
  product: PosProduct,
  item: ProductLineItem
): VariantDeposits {
  if (item.type !== "variant") return {};

  // Read from the catalog rather than the basket choice, like the product's own
  // deposit, so a basket restored from storage charges today's fee.
  return Object.fromEntries(
    item.options.flatMap((option) => {
      const catalogChoices =
        product.options.find((o) => o.id === option.id)?.choices ?? [];

      return option.choices.flatMap((choice) => {
        const deposit =
          choice.amount > 0
            ? catalogChoices.find((c) => c.id === choice.id)?.deposit
            : null;
        return deposit ? [[choice.id, deposit] as const] : [];
      });
    })
  );
}

function unitLineDeposit(
  product: PosProduct,
  item: ProductLineItem,
  variantDeposits: VariantDeposits
): UnitDeposit {
  const own = {
    inclVat: product.deposit?.feeInclVat ?? 0,
    exclVat: product.deposit?.feeExclVat ?? 0
  };
  if (item.type !== "variant") return own;

  return item.options
    .flatMap((option) => option.choices)
    .reduce((acc, choice) => {
      const deposit = variantDeposits[choice.id];
      if (!deposit) return acc;

      return {
        inclVat: acc.inclVat + deposit.feeInclVat * choice.amount,
        exclVat: acc.exclVat + deposit.feeExclVat * choice.amount
      };
    }, own);
}

/** The deposit sub-line's amount, on the same VAT base as the price above it. */
function formatDepositDisplayPrice(
  inclVat: number,
  exclVat: number,
  { showPricesInclVat, currency, locale }: DisplayContext
): string | undefined {
  if (inclVat === 0) return undefined;

  return priceFormatter({
    value: displayValue(showPricesInclVat, inclVat, exclVat),
    currency,
    locale
  });
}

function formatVariantDepositDisplayPrices(
  item: ProductLineItem,
  variantDeposits: VariantDeposits,
  ctx: DisplayContext
): Record<string, string> {
  if (item.type !== "variant") return {};

  return Object.fromEntries(
    item.options
      .flatMap((option) => option.choices)
      .flatMap((choice) => {
        const deposit = variantDeposits[choice.id];
        const units = choice.amount * item.count;
        const displayPrice =
          deposit &&
          formatDepositDisplayPrice(
            deposit.feeInclVat * units,
            deposit.feeExclVat * units,
            ctx
          );
        return displayPrice ? [[choice.id, displayPrice] as const] : [];
      })
  );
}

// Per-line context captured during prepare, consumed by `projectPosLine`. Plain
// data (not a closure) so the projection is named, typed, and unit-testable.
type PosLineMeta =
  | {
      kind: "custom";
      basketId: string;
      productId: string;
      name: string;
      quantity: number;
      unitPrice: number;
      category: string;
      productLineId: string;
      taxRateName: string | null;
      taxRateId: string | null;
      exclVatUnitPrice: number;
      vatRate: number;
      totalUnitPriceExclVatBase: number;
    }
  | {
      kind: "product";
      basketId: string;
      product: PosProduct;
      item: Exclude<ProductBasket, { type: "custom" }>;
      unitCompositeInclVat: number;
      unitPriceExclVat: number;
      hybridBillingDiscount: PosProduct["discount"] | null;
      hasHybridBillingDiscount: boolean;
      hybridPerUnitAmount: number;
      manualAmount: number | undefined;
      depositTotalExclVat: number;
      variantDeposits: VariantDeposits;
      stockStatus: PosProductInBasket["stockStatus"];
    };

type PreparedLine = {
  engineLine: SaleEngineLine;
  meta: PosLineMeta;
};

// Pure projection: the priced engine result + captured meta → a POS basket line.
function projectPosLine(
  meta: PosLineMeta,
  priced: SaleEnginePricedLine,
  ctx: DisplayContext
): PosProductInBasket {
  const { hasOrderDiscount, orderDiscount } = ctx;

  if (meta.kind === "custom") {
    return {
      depositTotal: 0,
      depositTotalExclVat: 0,
      id: meta.productId,
      name: meta.name,
      quantity: meta.quantity,
      unitPrice: meta.unitPrice,
      category: meta.category,
      basketId: meta.basketId,
      type: "custom",
      productLineId: meta.productLineId,
      unitSystem: "piece",
      discount: null,
      taxRateName: meta.taxRateName,
      taxRateId: meta.taxRateId,
      userLimitId: null,
      totalUnitPrice: meta.unitPrice,
      ...pricedDisplayFields(priced, meta.totalUnitPriceExclVatBase, ctx),
      exclVatUnitPrice: meta.exclVatUnitPrice,
      vatRate: meta.vatRate,
      stockStatus: "unlimited",
      ...(hasOrderDiscount
        ? {
            discountUnitPrice: priced.orderDiscount,
            discountPercentage: orderDiscountPercentage(
              orderDiscount,
              priced.orderDiscount,
              priced.lineTotalBeforeDiscount
            )
          }
        : {})
    };
  }

  const {
    product,
    item,
    unitCompositeInclVat,
    unitPriceExclVat,
    hybridBillingDiscount,
    hasHybridBillingDiscount,
    hybridPerUnitAmount,
    manualAmount,
    stockStatus
  } = meta;

  let discountUnitPrice: number | undefined;
  let discountPercentage = 0;
  if (hasOrderDiscount) {
    discountUnitPrice = priced.orderDiscount;
    discountPercentage = orderDiscountPercentage(
      orderDiscount,
      priced.orderDiscount,
      priced.lineTotalBeforeDiscount
    );
  } else if (manualAmount) {
    // The capped amount: what the line charges is what the order declares.
    discountUnitPrice = priced.flatDiscount;
    discountPercentage = hasHybridBillingDiscount
      ? (priced.flatDiscount /
          ((unitCompositeInclVat - hybridPerUnitAmount) * item.count)) *
        100
      : (priced.flatDiscount / (unitCompositeInclVat * item.count)) * 100;
  }

  // A unitPrice line claims only what the engine deducted: 0 once the
  // allowance is spent or an order discount replaces it, because the server
  // refuses a larger claim.
  const discount =
    hybridBillingDiscount?.allowanceType === "unitPrice"
      ? { ...hybridBillingDiscount, amount: priced.allowanceDeduction }
      : hybridBillingDiscount;

  const baseProduct = {
    id: product.id,
    productLineId: product.productLineId,
    name: product.name,
    description: product.description,
    unitPrice: product.unitPrice,
    category: product.category,
    quantity: item.count,
    basketId: item.id,
    ...pricedDisplayFields(priced, unitPriceExclVat * item.count, ctx),
    totalUnitPrice: unitCompositeInclVat,
    vatRate: product.vatRate,
    exclVatUnitPrice: product.exclVatUnitPrice,
    taxRateName: product.taxRateName,
    taxRateId: product.taxRateId,
    stampCardRuleId: item.stampCardRuleId,
    discountUnitPrice,
    discountPercentage,
    discount,
    userLimitId: product.userLimitId,
    stockStatus,
    depositTotal: priced.depositTotal,
    depositTotalExclVat: meta.depositTotalExclVat,
    depositDisplayPrice: formatDepositDisplayPrice(
      (product.deposit?.feeInclVat ?? 0) * item.count,
      (product.deposit?.feeExclVat ?? 0) * item.count,
      ctx
    ),
    variantDepositDisplayPrices: formatVariantDepositDisplayPrices(
      item,
      meta.variantDeposits,
      ctx
    ),
    deposit: product.deposit,
    variantDeposits: meta.variantDeposits
  };

  if (item.type === "regular") {
    return { ...baseProduct, type: item.type, unitSystem: "piece" };
  }

  if (item.type === "variant") {
    return {
      ...baseProduct,
      options: item.options,
      type: item.type,
      unitSystem: "piece"
    };
  }

  return {
    ...baseProduct,
    unitPrice: calculateUnitPriceByUnitSystem({
      unitPrice: product.unitPrice,
      weight: item.options[0].choices[0].weight,
      unitSystem: product.unitSystem,
      weightUnit: item.options[0].choices[0].weightUnit
    }),
    exclVatUnitPrice: calculateUnitPriceByUnitSystem({
      unitPrice: product.exclVatUnitPrice,
      weight: item.options[0].choices[0].weight,
      unitSystem: product.unitSystem,
      weightUnit: item.options[0].choices[0].weightUnit
    }),
    options: item.options,
    type: item.type,
    unitSystem: item.unitSystem
  };
}

export function computeProductsInBasket({
  basket,
  products,
  discounts,
  allowances,
  orderDiscount,
  defaultTaxRate,
  currency,
  locale,
  showPricesInclVat
}: ComputeProductsInBasketArgs): ComputeProductsInBasketResult {
  const hasOrderDiscount = Boolean(orderDiscount.amount);
  const displayContext: DisplayContext = {
    showPricesInclVat,
    currency,
    locale,
    hasOrderDiscount,
    orderDiscount
  };

  const prepared = basket.flatMap<PreparedLine>((item) => {
    if (item.count === 0) {
      return [];
    }

    if (item.type === "custom") {
      const customVatRate = item.customVatRate;
      const vatRate = customVatRate || defaultTaxRate.vatRate;

      const exclVatUnitPrice =
        item.vatCalculation === "excl"
          ? item.rawUnitPrice
          : item.unitPrice / (1 + vatRate);

      const unitCompositeInclVat =
        item.vatCalculation === "excl"
          ? item.rawUnitPrice * (1 + vatRate)
          : item.unitPrice;

      const engineLine: SaleEngineLine = {
        basketId: item.id,
        quantity: item.count,
        unitCompositeInclVat,
        eligibleForOrderDiscount: item.unitPrice > 0,
        discounts: [],
        allowanceDiscountIds: []
      };

      const meta: PosLineMeta = {
        kind: "custom",
        basketId: item.id,
        productId: item.productId,
        name: item.name,
        quantity: item.count,
        unitPrice: item.unitPrice,
        category: item.category,
        productLineId: item.id,
        taxRateName: customVatRate ? null : defaultTaxRate.name,
        taxRateId: customVatRate ? null : defaultTaxRate.id,
        exclVatUnitPrice,
        vatRate,
        totalUnitPriceExclVatBase: exclVatUnitPrice * item.count
      };

      return [{ engineLine, meta }];
    }

    const product = products.find((p) => p.id === item.productId);
    if (!product) {
      return [];
    }

    const stockStatus = inferStockStatusFromBasket({
      product,
      basket
    });

    let inclPerUnitBase = product.unitPrice;
    let unitPriceExclVat = product.exclVatUnitPrice;

    if (item.type === "variant") {
      const unitPriceExclVatTotalVariants = calculateVariantTotal({
        variants: item.options,
        showPricesInclVat: false
      });
      unitPriceExclVat += unitPriceExclVatTotalVariants;
    }

    if (item.type === "variant-weight") {
      inclPerUnitBase = calculateUnitPriceByUnitSystem({
        unitPrice: product.unitPrice,
        weight: item.options[0].choices[0].weight,
        unitSystem: product.unitSystem,
        weightUnit: item.options[0].choices[0].weightUnit
      });
      unitPriceExclVat = calculateUnitPriceByUnitSystem({
        unitPrice: product.exclVatUnitPrice,
        weight: item.options[0].choices[0].weight,
        unitSystem: product.unitSystem,
        weightUnit: item.options[0].choices[0].weightUnit
      });
    }

    // Per-unit composite: base + each option choice rounded separately (server
    // `calculateConfigTotal`); engine does ×qty.
    const variantsPriceInclVatRounded =
      item.type === "variant" ? sumOptionChoicesInclVat(item.options) : 0;
    const unitCompositeInclVat =
      roundAwayFromZero(inclPerUnitBase) + variantsPriceInclVatRounded;

    const hybridBillingDiscount = item.isHybridBilled ? product.discount : null;
    const hasHybridBillingDiscount =
      hybridBillingDiscount?.type === "discount-hybridBilling" &&
      hybridBillingDiscount?.allowanceType !== "unitPrice";
    const hybridPerUnitAmount = hybridBillingDiscount?.amount ?? 0;
    const manualAmount = item.discountUnitPrice;

    const lineDiscounts: SaleEngineLineDiscount[] = [];
    if (hasHybridBillingDiscount) {
      lineDiscounts.push(
        product.discount?.percentage && product.discount.percentage > 0
          ? { kind: "percentage", amount: product.discount.percentage }
          : { kind: "currency", amount: product.discount?.amount ?? 0 }
      );
    }
    if (manualAmount) {
      lineDiscounts.push({ kind: "flat", amount: manualAmount });
    }

    const allowanceDiscountIds = hybridBillingDiscount?.id
      ? [hybridBillingDiscount.id]
      : [];

    const variantDeposits = pickedVariantDeposits(product, item);
    const unitDeposit = unitLineDeposit(product, item, variantDeposits);

    // POS policy: order discount replaces per-line + allowance discounts — feed
    // the engine none of them then.
    const engineLine: SaleEngineLine = {
      basketId: item.id,
      quantity: item.count,
      unitCompositeInclVat,
      eligibleForOrderDiscount: inclPerUnitBase > 0,
      discounts: hasOrderDiscount ? [] : lineDiscounts,
      allowanceDiscountIds: hasOrderDiscount ? [] : allowanceDiscountIds,
      unitDepositInclVat: unitDeposit.inclVat
    };

    // No pricing stage touches the deposit, so its excl base needs none of the
    // scaling `discountedExclVat` does for discounted product money.
    const depositTotalExclVat = unitDeposit.exclVat * item.count;

    const meta: PosLineMeta = {
      kind: "product",
      basketId: item.id,
      product,
      item,
      unitCompositeInclVat,
      unitPriceExclVat,
      depositTotalExclVat,
      variantDeposits,
      hybridBillingDiscount,
      hasHybridBillingDiscount,
      hybridPerUnitAmount,
      manualAmount,
      stockStatus
    };

    return [{ engineLine, meta }];
  });

  const engineLines = prepared.map((p) => p.engineLine);
  const priced = computeBasket(engineLines, {
    allowances,
    discounts,
    orderDiscount
  });

  // Project by basketId, not array position — `SaleEnginePricedLine` carries it.
  const pricedByBasket = new Map(priced.map((p) => [p.basketId, p]));
  const productsInBasket = prepared.flatMap((p) => {
    const pricedLine = pricedByBasket.get(p.engineLine.basketId);
    return pricedLine
      ? [projectPosLine(p.meta, pricedLine, displayContext)]
      : [];
  });

  // Pre-(allowance/order)-discount total: post-per-line total, rounded per line.
  const totalUnit = priced.reduce(
    (acc, p) =>
      acc + roundAwayFromZero(p.lineTotalBeforeDiscount - p.perLineDiscount),
    0
  );

  const sumRounded = (key: "totalPrice" | "totalUnitPriceExclVat") =>
    productsInBasket.reduce(
      (acc, product) => acc + roundAwayFromZero(product[key]),
      0
    );

  return {
    productsInBasket,
    totalUnit,
    totalUnitDiscounted: sumRounded("totalPrice"),
    totalUnitExclVatDiscounted: sumRounded("totalUnitPriceExclVat"),
    // Kept apart from the product totals: the payable adds it, no stage reduces it.
    totalDeposit: productsInBasket.reduce(
      (acc, product) => acc + product.depositTotal,
      0
    ),
    totalDepositExclVat: productsInBasket.reduce(
      (acc, product) => acc + product.depositTotalExclVat,
      0
    ),
    totalOrderDiscount: priced.reduce((acc, p) => acc + p.orderDiscount, 0),
    totalOrderDiscountable: orderDiscountableTotal(engineLines)
  };
}
